import axios, { AxiosInstance } from 'axios';
import {
  ApiResponse,
  VendorMetrics,
  Product,
  Order,
  Comment,
  Vendor,
  ProductFormData,
  VendorFormData,
  DashboardStats,
} from '../types/api';

declare global {
  interface Window {
    API_BASE_URL: string;
  }
}

// DEMO_MODE removed - now using real API data from enhanced backend

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: window.API_BASE_URL || 'https://api.erp.oroud.net',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response.data,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          // window.location.href = '/login'; // Disabled redirect to login
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(email: string, password: string) {
    return this.api.post('/api/auth.php?action=login', { email, password });
  }

  async getCurrentUser() {
    return this.api.get('/api/auth.php?action=me');
  }

  async logout() {
    return this.api.post('/api/auth.php?action=logout');
  }

  // User Management
  async getUsers() {
    return this.api.get('/api/auth.php?action=users');
  }

  async createUser(userData: any) {
    return this.api.post('/api/auth.php?action=register', userData);
  }

  async updateUser(userId: number, userData: any) {
    return this.api.put(`/api/auth.php?action=update_user&user_id=${userId}`, userData);
  }

  async deleteUser(userId: number) {
    return this.api.delete(`/api/auth.php?action=delete_user&user_id=${userId}`);
  }

  // Role Management
  async getRoles() {
    return this.api.get('/api/auth.php?action=roles');
  }

  async createRole(roleData: any) {
    return this.api.post('/api/auth.php?action=create_role', roleData);
  }

  async updateRole(roleId: number, roleData: any) {
    return this.api.put(`/api/auth.php?action=update_role&role_id=${roleId}`, roleData);
  }

  async deleteRole(roleId: number) {
    return this.api.delete(`/api/auth.php?action=delete_role&role_id=${roleId}`);
  }

  // Activity & Security
  async getActivity(limit = 100) {
    return this.api.get(`/api/auth.php?action=activity&limit=${limit}`);
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.api.post('/api/auth.php?action=change_password', {
      current_password: currentPassword,
      new_password: newPassword
    });
  }

  async register(data: { name: string; email: string; password: string; role: string }, token: string) {
    return this.api.post('/api/auth.php?action=register', data, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  // Orders
  async getOrders() {
    return this.api.get('/api/orders.php');
  }

  async getOrdersFromApi() {
    // Always fetch from the real API, not demo
    return this.api.get('/api/orders.php');
  }

  async getOrder(id: number) {
    return this.api.get(`/api/orders.php?id=${id}`);
  }

  async updateOrderStatus(id: number, status: string) {
    return this.api.put(`/api/orders.php?id=${id}`, { status });
  }

  // Paginated Orders
  async getOrdersPaginated(params: Record<string, any>) {
    const query = Object.entries(params)
      .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
      .join('&');
    return this.api.get(`/api/orders.php?${query}`);
  }

  // Products
  async getProducts() {
    return this.api.get('/api/products.php');
  }

  async getProduct(id: number) {
    return this.api.get(`/api/products.php?id=${id}`);
  }

  async getProductsByVendor(vendorId: number) {
    return this.api.get(`/api/products.php?vendor_id=${vendorId}`);
  }

  // Products CRUD
  async createProduct(data: ProductFormData) {
    return this.api.post('/api/products.php', data);
  }
  async updateProduct(id: number, data: ProductFormData) {
    return this.api.put(`/api/products.php?id=${id}`, data);
  }
  async deleteProduct(id: number) {
    return this.api.delete(`/api/products.php?id=${id}`);
  }

  // Vendors
  async getVendors() {
    // Fetch all vendors with comprehensive data including orders, products, revenue
    try {
      console.log('🔄 Making API call to /api/vendors.php');
      const response = await this.api.get('/api/vendors.php');
      const data = response as any;
      
      console.log('✅ API Response received:', {
        totalVendors: data?.total,
        vendorsArrayLength: data?.vendors?.length,
        status: data?.status,
        debug: data?.debug,
        hasVendors: !!data?.vendors,
        responseKeys: Object.keys(data || {})
      });
      
      // If no vendors found, use test data temporarily
      if (!data.vendors || data.vendors.length === 0) {
        console.log('⚠️ No vendors found from API, using test data');
        return await this.api.get('/api/vendors.php?test=1');
      }
      
      console.log('🎉 Returning real vendor data with', data.vendors.length, 'vendors');
      return response;
    } catch (error) {
      console.error('❌ Error fetching vendors, using test data:', error);
      // Fallback to test data if API fails
      return await this.api.get('/api/vendors.php?test=1');
    }
  }

  async getVendor(id: number) {
    // Fetch basic vendor information only (fast)
    return this.api.get(`/api/vendors.php?vendor_id=${id}`);
  }

  async getVendorDetails(id: number) {
    // Fetch comprehensive vendor details (slower - includes orders, products, payments)
    return this.api.get(`/api/vendors.php?vendor_id=${id}&action=details`);
  }

  async getVendorOrders(vendorId: number) {
    // Get all orders for a specific vendor (optimized endpoint)
    return this.api.get(`/api/vendors.php?vendor_id=${vendorId}&action=orders`);
  }

  async getVendorProducts(vendorId: number) {
    // Get all products for a specific vendor (optimized endpoint)
    return this.api.get(`/api/vendors.php?vendor_id=${vendorId}&action=products`);
  }

  async getVendorAnalytics(vendorId: number) {
    // Get vendor analytics including monthly revenue
    const response = await this.getVendorDetails(vendorId);
    const vendor = response as any;
    return {
      data: {
        monthlyRevenue: vendor.monthlyRevenue || [],
        totalRevenue: vendor.totalRevenue || 0,
        totalOrders: vendor.totalOrders || 0,
        totalProducts: vendor.products?.length || 0
      },
      status: 'success'
    };
  }

  async getVendorPaymentMethods(vendorId: number) {
    // Get vendor payment methods/bank accounts (optimized endpoint)
    return this.api.get(`/api/vendors.php?vendor_id=${vendorId}&action=payments`);
  }

  // Vendors CRUD
  async createVendor(data: VendorFormData) {
    return this.api.post('/api/vendors.php', data);
  }

  async updateVendor(id: number, data: VendorFormData) {
    return this.api.put(`/api/vendors.php?id=${id}`, data);
  }

  async deleteVendor(id: number) {
    return this.api.delete(`/api/vendors.php?id=${id}`);
  }

  async bulkDeleteVendors(ids: number[]) {
    // For bulk delete, we'll call delete for each vendor
    const promises = ids.map(id => this.deleteVendor(id));
    return Promise.all(promises);
  }

  // Comments
  async getComments(orderId: number) {
    return this.api.get(`/api/comments.php?id=${orderId}`);
  }

  async addComment(orderId: number, comment: string, is_customer_note: boolean) {
    return this.api.post('/api/comments.php', { order_id: orderId, comment, is_customer_note });
  }

  async deleteComment(noteId: number, orderId: number, token: string) {
    return this.api.delete(`/api/comments.php?id=${noteId}&order_id=${orderId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  // Dashboard
  async getDashboardStats() {
    return this.api.get('/api/dashboard.php');
  }

  // New optimized dashboard with KPIs and date filters
  async getDashboard(
    dateFrom?: string, 
    dateTo?: string, 
    forceRefresh = false, 
    metaDatePreset = 'last_7d',
    metaDateFrom?: string,
    metaDateTo?: string
  ): Promise<DashboardStats> {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (forceRefresh) params.append('refresh', 'true');
    params.append('meta_date_preset', metaDatePreset);
    if (metaDateFrom) params.append('meta_date_from', metaDateFrom);
    if (metaDateTo) params.append('meta_date_to', metaDateTo);
    
    const query = params.toString();
    const url = `/api/dashboard.php${query ? `?${query}` : ''}`;
    
    return this.api.get(url);
  }

  async clearCache() {
    return this.api.get('/api/clear_cache.php');
  }

  // Meta Marketing API
  async getMetaData(datePreset = 'today', dateFrom?: string, dateTo?: string, forceRefresh = false) {
    const params = new URLSearchParams();
    params.append('date_preset', datePreset);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (forceRefresh) params.append('refresh', 'true');
    
    const query = params.toString();
    const url = `/api/meta.php${query ? `?${query}` : ''}`;
    
    return this.api.get(url);
  }

  // Accounting & Vendor Payments
  async getUnpaidOrders() {
    return this.api.get('/api/accounting.php?action=unpaid_orders');
  }

  async getPaymentBatches(page = 1, perPage = 20) {
    return this.api.get(`/api/accounting.php?action=payment_batches&page=${page}&per_page=${perPage}`);
  }

  async getPaymentBatchDetails(batchId: number) {
    return this.api.get(`/api/accounting.php?action=payment_batch&batch_id=${batchId}`);
  }

  async getVendorPayments(vendorId: string) {
    return this.api.get(`/api/accounting.php?action=vendor_payments&vendor_id=${vendorId}`);
  }

  async getPaymentSummary() {
    return this.api.get('/api/accounting.php?action=payment_summary');
  }

  async getVendorPaymentSummary() {
    return this.api.get('/api/accounting.php?action=vendor_payment_summary');
  }

  async getWeeklyPaymentReport() {
    return this.api.get('/api/accounting.php?action=weekly_report');
  }

  async createPaymentBatch(data: {
    order_ids: number[];
    batch_name: string;
    payment_date: string;
    notes?: string;
    created_by: number;
  }) {
    return this.api.post('/api/accounting.php', {
      action: 'create_payment_batch',
      ...data,
    });
  }

  async processPayments(data: {
    payment_ids: number[];
    payment_method: string;
    payment_reference?: string;
    notes?: string;
    performed_by: number;
  }) {
    return this.api.post('/api/accounting.php', {
      action: 'process_payment',
      ...data,
    });
  }

  async updatePaymentStatus(data: {
    payment_id: number;
    status: string;
    notes?: string;
    performed_by: number;
  }) {
    return this.api.post('/api/accounting.php', {
      action: 'update_payment_status',
      ...data,
    });
  }

  async deletePaymentBatch(data: {
    batch_id: number;
    performed_by: number;
  }) {
    return this.api.post('/api/accounting.php', {
      action: 'delete_payment_batch',
      ...data,
    });
  }

  async completeBatch(data: {
    batch_id: number;
    performed_by: number;
  }) {
    return this.api.post('/api/accounting.php', {
      action: 'complete_batch',
      ...data,
    });
  }
}

const api = new ApiService();
export default api;